// Animate Static Ad's form (live's `AnimateStaticAdForm`, spec G14-templates): the static ad, an
// optional animation direction and the clip's length; the server's motion designer writes the
// prompt, then live's image to video model (its default, else its fallback) animates the ad.
// One tile.
import { Group, Stack, Text, Textarea } from "@mantine/core";
import { Clock3 } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { CatalogVideoModel } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import {
  exampleImage,
  ImageSlot,
  ReuseSwitch,
  slotSource,
  StartArrow,
  type FormExample,
  type SlotImage,
  type TemplateFormProps,
} from "./kit";
import { CostChip } from "../TemplateCost";

/** Live's animation model (`DEFAULT_IMAGE_TO_VIDEO_MODEL_ID`), and its words when no model can animate. */
const VIDEO_MODEL = "minimax-h3-max-turbo";
const VIDEO_MISSING = "No image-to-video model is available.";
/** The direction's bound (`ANIMATE_PROMPT_CHARS`, the motion step's prompt). */
const PROMPT_MAX = 32000;
/** Live's words (`MOTION_PROMPT_PLACEHOLDER`, `SOURCE_IMAGE_TOOLTIP`), verbatim. */
const PLACEHOLDER = "Animation direction (optional) — e.g. slow zoom on the product, drifting clouds…";
interface Values {
  image: SlotImage | null;
  prompt: string;
  /** Seconds, as the select holds them; "" for the model's default. */
  duration: string;
}

/** The form's values: the card's example's (its ad, live's direction and length), or empty. */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "animate-static-ad") return null;
  return {
    image: exampleImage(example, inputs.image),
    prompt: inputs.prompt ?? "",
    duration: inputs.duration == null ? "" : String(inputs.duration),
  };
}

/**
 * The model the clip runs on, as live's `getImageToVideoModel` and the server's start pick it: live's
 * default where the catalog offers it, else the first model in the list that animates an image.
 */
function animatorOf(models: readonly CatalogVideoModel[]): CatalogVideoModel | undefined {
  return (
    models.find((each) => each.id === VIDEO_MODEL) ??
    models.find((each) => each.job_types.includes("video.image"))
  );
}

/**
 * Live's form: "Reuse example" (on fills the card's example, off clears the form; Replicate opens
 * it on), the "Image" slot ("Pick the static ad to animate"), the animation direction (optional),
 * and in the footer the length (the model's, its default first chosen: "How long the animated
 * video should run.") and the round start, off until there is an ad and while no model can
 * animate it.
 */
export function AnimateStaticAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [image, setImage] = useState<SlotImage | null>(start?.image ?? null);
  const [prompt, setPrompt] = useState(start?.prompt ?? "");
  const [duration, setDuration] = useState(start?.duration ?? "");
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setImage(values?.image ?? null);
    setPrompt(values?.prompt ?? "");
    setDuration(values?.duration ?? "");
  };
  const catalog = useGetCatalog().data;
  const model = catalog === undefined ? undefined : animatorOf(catalog.video_models);
  const missing = catalog !== undefined && model === undefined;
  const lengths = model?.modes["video.image"]?.durations ?? model?.durations ?? null;
  // The chosen length, or the model's default where none is chosen or the model lacks it (an
  // example's 5 s on a fallback model that makes 4 or 8).
  const chosen = Number(duration);
  const seconds = lengths?.values.includes(chosen) ? chosen : (lengths?.default ?? null);
  const inputs =
    image === null
      ? null
      : {
          template: "animate-static-ad" as const,
          image: slotSource(image),
          prompt,
          ...(seconds === null ? {} : { duration: seconds }),
        };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success && !missing ? quote.data : undefined,
    request: request.success && !missing ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">Animate Static Ad</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Choice
            variant="footer"
            setting="Duration"
            icon={<Clock3 size={16} strokeWidth={1.5} />}
            tip="How long the animated video should run."
            width={65}
            value={seconds === null ? "" : String(seconds)}
            options={(lengths?.values ?? []).map((each) => ({ value: String(each), label: `${each}s` }))}
            onChange={setDuration}
          />
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={1} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <ImageSlot
          teamId={teamId}
          brandId={brandId}
          label="Image"
          title="Pick the static ad to animate"
          value={image}
          onChange={setImage}
        />
        <Textarea
          variant="unstyled"
          aria-label="Animation direction"
          placeholder={PLACEHOLDER}
          autosize
          minRows={1}
          maxRows={6}
          maxLength={PROMPT_MAX}
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
        />
        {missing ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {VIDEO_MISSING}
          </Text>
        ) : null}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
